<script setup>
import { ref, inject } from 'vue'
import { onClickOutside } from '@vueuse/core'

import IconButton from '@/components/IconButton.vue'
import IconClose from '@/components/icons/IconClose.vue'

const emits = defineEmits(['close'])
defineProps({
  message: {
    type: String,
    required: true
  },
  active: {
    type: Boolean,
    required: true
  },
  /**
   * Sets which part of the COACHMARK should be anchored to the parent
   *  'top-left' | 'top-center' | 'top-right' |
   *  'right-top' | 'right-center' | 'right-bottom' |
   *  'bottom-right' | 'bottom-center' | 'bottom-left' |
   *  'left-bottom' | 'left-center' | 'left-top'
   */
  alignment: {
    type: String,
    default: 'top-left'
  }
})

const { advance } = inject('coachmarks', {
  advance: () => {},
  skip: () => {}
})

const handleClick = () => {
  advance()
  emits('close')
}

const coachmarkEl = ref(null)
onClickOutside(coachmarkEl, handleClick)
</script>

<template>
  <div class="relative">
    <slot />
    <div v-if="active" ref="coachmarkEl" :class="alignment" class="coachmark-body absolute z-40 bg-purple rounded-lg p-4 grid grid-flow-col gap-x-8 justify-between">
      <div class="flex flex-col gap-y-4 justify-center max-w-96">
        <p class=" font-medium text-xl">{{ message }}</p>
        <slot name="coachmarkControls" />
      </div>
      <div>
        <IconButton class="coachmark-close" @click="handleClick">
          <IconClose class="w-4 h-4 fill-cream"/>
        </IconButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.coachmark-body {
  grid-template-columns: repeat(2, max-content);
  box-shadow: 4px 4px 4px rgb(0 0 0 / 25%);
}

.coachmark-body:after {
  content: ' ';
  @apply w-0 h-0 absolute;
}

.coachmark-close {
  box-shadow: none !important;
}

.top-left { @apply top-full left-0 mt-4; }
.top-left:after { @apply border-b-[1rem] border-x-[1rem] border-b-purple border-x-transparent -top-3 left-8 -translate-x-1/2; }

.top-center { @apply top-full left-1/2 -translate-x-1/2 mt-4; }
.top-center:after { @apply border-b-[1rem] border-x-[1rem] border-b-purple border-x-transparent -top-3 left-1/2 -translate-x-1/2; }

.top-right { @apply top-full right-0 mt-4; }
.top-right:after { @apply border-b-[1rem] border-x-[1rem] border-b-purple border-x-transparent -top-3 right-8 translate-x-1/2; }

.right-top { @apply right-full top-0 mr-4; }
.right-top:after { @apply border-l-[1rem] border-y-[1rem] border-l-purple border-y-transparent -right-3 top-8 -translate-y-1/2; }

.right-center { @apply right-full top-1/2 -translate-y-1/2 mr-4; }
.right-center:after { @apply border-l-[1rem] border-y-[1rem] border-l-purple border-y-transparent -right-3 top-1/2 -translate-y-1/2; }

.right-bottom { @apply right-full bottom-0 mr-4; }
.right-bottom:after { @apply border-l-[1rem] border-y-[1rem] border-l-purple border-y-transparent -right-3 bottom-8 translate-y-1/2; }

.bottom-right { @apply bottom-full right-0 mb-4; }
.bottom-right:after { @apply border-t-[1rem] border-x-[1rem] border-t-purple border-x-transparent -bottom-3 right-8 translate-x-1/2; }

.bottom-center { @apply bottom-full left-1/2 -translate-x-1/2 mb-4; }
.bottom-center:after { @apply border-t-[1rem] border-x-[1rem] border-t-purple border-x-transparent -bottom-3 left-1/2 -translate-x-1/2; }

.bottom-left { @apply bottom-full left-0 mb-4; }
.bottom-left:after { @apply border-t-[1rem] border-x-[1rem] border-t-purple border-x-transparent -bottom-3 left-8 -translate-x-1/2; }

.left-bottom { @apply left-full bottom-0 ml-4; }
.left-bottom:after { @apply border-r-[1rem] border-y-[1rem] border-r-purple border-y-transparent -left-3 bottom-8 translate-y-1/2; }

.left-center { @apply left-full top-1/2 -translate-y-1/2 ml-4; }
.left-center:after { @apply border-r-[1rem] border-y-[1rem] border-r-purple border-y-transparent -left-3 top-1/2 -translate-y-1/2; }

.left-top { @apply left-full top-0 ml-4; }
.left-top:after { @apply border-r-[1rem] border-y-[1rem] border-r-purple border-y-transparent -left-3 top-8 -translate-y-1/2; }
</style>
